import { useState, useRef, useEffect, useMemo, memo } from "react";
import ChatItem from "./chat-item";
import ChatInput from "./chat-input";
import {
  useChatHistoryQuery,
  useSendMessageMutation,
} from "@/graphql/generated";
import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import { useTranslation } from "react-i18next";
import {
  readMessage,
  selectCurrentRoom,
} from "@/context/features/room/roomSlice";
import { useDispatch, useSelector } from "react-redux";
import { useCachedQuery } from "@/graphql/useCached";
import { ConnectionState } from "livekit-client";
import {
  clearOpenedGroup,
  selectCurrentChat,
} from "@/context/features/chat/chatSlice";
import { TGroup } from "@/types/public";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toaster } from "@/utils/toaster";

export interface Message {
  id: string;
  sender: string;
  groupId: string;
  content: string;
  isLocal: boolean;
  timestamp: Date;
}

interface Props {
  gp?: TGroup;
}

function ChatListComponent({ gp }: Props) {
  const { t, i18n } = useTranslation();
  const { roomData } = useSelector(selectCurrentRoom);
  const { openedGroup } = useSelector(selectCurrentChat);
  const dispatch = useDispatch();
  const { data, loading, error } = useCachedQuery(useChatHistoryQuery, {
    variables: openedGroup?.id ? { groupId: openedGroup.id } : undefined,
    skip: !openedGroup?.id,
  });

  const messages = useMemo(
    () => data?.chat.chatHistory || [],
    [data?.chat.chatHistory]
  );
  const [inputMessage, setInputMessage] = useState("");

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [sendMessage] = useSendMessageMutation();
  const { livekitContext } = useLivekitContext();
  const room = livekitContext?.room;

  const canSendChat = room?.state === ConnectionState.Connected;

  const handleSendMessage = () => {
    if (inputMessage.trim() === "") return;
    onSend();
  };

  const onSend = async () => {
    const messageText = inputMessage.trim();
    if (!messageText || !roomData?.name || !openedGroup) return;
    if (!canSendChat) {
      toaster("error", t("chat_not_connected"));
      return;
    }
    try {
      await sendMessage({
        variables: {
          text: messageText,
          groupId: openedGroup.id,
        },
      });
      setInputMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
      toaster("error", t("chat_send_failed"));
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (openedGroup?.id) dispatch(readMessage(openedGroup.id));
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
    return () => clearTimeout(timeout);
  }, [messages.length, dispatch, openedGroup?.id]);

  if (loading)
    return (
      <div className="flex-1 flex items-center justify-center">
        {t("loading_messages")}...
      </div>
    );

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-red-500">
        {t("loading_messages_error")}...
      </div>
    );

  return (
    <div className="flex flex-col flex-1 overflow-hidden min-h-0 relative">
      {gp && (
        <div className="p-3 font-semibold text-primary flex gap-1 items-center border-b shadow">
          {i18n.language === "fa" ? (
            <ArrowRight
              className="hover:scale-110 cursor-pointer"
              onClick={() => dispatch(clearOpenedGroup())}
            />
          ) : (
            <ArrowLeft
              className="hover:scale-110 cursor-pointer"
              onClick={() => dispatch(clearOpenedGroup())}
            />
          )}

          <span>{t("chat_with") + ` "${gp.name}"`}</span>
        </div>
      )}

      <div className="w-full flex-1 overflow-y-auto min-h-52 px-4 space-y-2 scrollbar-thin scrollbar-thumb-muted-foreground/60 scrollbar-track-transparent pb-20 pt-2">
        {messages.length === 0 && (
          <div className="flex-1 flex items-center justify-center mt-10">
            <span className="text-muted-foreground">{t("no_messages")}</span>
          </div>
        )}
        {messages
          .filter(
            (message) =>
              String(message.groupId) === String(openedGroup?.id ?? "")
          )
          .map((message, index) => (
            <ChatItem
              key={`${message.senderId}-${message.timestamp}-${index}`}
              message={message}
              isLocal={
                livekitContext?.localParticipant?.identity === message.senderId
              }
            />
          ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="w-full backdrop-blur-xl px-4 py-3 flex items-end gap-3 absolute bottom-0 end-0">
        <ChatInput
          handleSendMessage={handleSendMessage}
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          textareaRef={textareaRef}
          disabled={!canSendChat}
        />
      </div>
    </div>
  );
}

const ChatList = memo(ChatListComponent);
export default ChatList;
